"use client";

import Search from "@repo/icons/react/Search";
import CurrencyDollar from "@repo/icons/react/CurrencyDollar";
import ShieldCheck from "@repo/icons/react/ShieldCheck";
import UserSearch from "@repo/icons/react/UserSearch";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

export function HowItWorks() {
  const t = useTranslations();
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const steps = [
    {
      num: 1,
      Icon: Search,
      title: t("tools.valuation.howItWorks.info.title"),
      description: t("tools.valuation.howItWorks.info.description"),
    },
    {
      num: 2,
      Icon: CurrencyDollar,
      title: t("tools.valuation.howItWorks.calculate.title"),
      description: t("tools.valuation.howItWorks.calculate.description"),
    },
    {
      num: 3,
      Icon: ShieldCheck,
      title: t("tools.valuation.howItWorks.estimate.title"),
      description: t("tools.valuation.howItWorks.estimate.description"),
    },
    {
      num: 4,
      Icon: UserSearch,
      title: t("tools.valuation.howItWorks.next.title"),
      description: t("tools.valuation.howItWorks.next.description"),
    },
  ];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      setProgress(1);
      return;
    }

    let raf = 0;

    const updateProgress = () => {
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      const start = viewportH * 0.88;
      const end = viewportH * 0.1;
      const raw = (start - rect.top) / (start - end);
      setProgress(Math.max(0, Math.min(raw, 1)));
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        updateProgress();
        raf = 0;
      });
    };

    updateProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  const stepRatio = (index: number) => index / (steps.length - 1);
  const boostedProgress = Math.min(1, progress * 1.45);
  const timelineProgress =
    hoveredIndex === null
      ? boostedProgress
      : Math.max(progress, stepRatio(hoveredIndex));

  const stepActivation = (index: number) => {
    const ratio = stepRatio(index);
    return Math.max(0, Math.min((timelineProgress - ratio + 0.22) / 0.26, 1));
  };

  const accents = ["success", "theme-blue", "highlight", "#00A86B"] as const;

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-b from-background via-secondary to-theme-green-soft p-5 md:p-8"
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-[0.1]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(0,0,0,0.08) 0 1px, transparent 1px 30px)",
          }}
        />
      </div>

      <div className="relative flex flex-col gap-8 md:gap-10">
        <h2 className="text-center text-2xl font-semibold md:text-4xl">
          {t("tools.valuation.howItWorks.title")}
        </h2>

        <div className="hidden md:block">
          <div className="relative mb-7 h-10 px-8">
            <div className="absolute left-8 right-8 top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-border" />
            <div
              className="absolute left-8 top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-foreground transition-[width] duration-300"
              style={{ width: `calc(${timelineProgress * 100}% - 4rem)` }}
            />
            {steps.map((step, idx) => {
              const activation = stepActivation(idx);
              return (
                <div
                  key={step.num}
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${stepRatio(idx) * 100}%` }}
                >
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full border bg-card font-data text-xs font-semibold transition-all duration-300"
                    style={{
                      borderColor:
                        activation > 0.5
                          ? "var(--foreground)"
                          : "var(--border)",
                      boxShadow:
                        activation > 0.8
                          ? `0 0 0 6px ${accents[idx]}22`
                          : "0 0 0 0 rgba(0,0,0,0)",
                    }}
                  >
                    {step.num}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-4 gap-4">
            {steps.map((step, idx) => {
              const activation = stepActivation(idx);
              return (
                <article
                  key={step.num}
                  className="rounded-xl border bg-card/96 p-5 transition-all duration-400"
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    borderColor:
                      activation > 0.7 ? `${accents[idx]}66` : "var(--border)",
                    opacity: Math.max(activation, 0.35),
                    transform: `translateY(${(1 - activation) * 10}px)`,
                  }}
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${accents[idx]}1A` }}
                    >
                      <step.Icon className="h-4 w-4 text-foreground/80" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                      Step {step.num}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold leading-tight text-foreground">
                    {step.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>

        <div className="space-y-4 md:hidden">
          {steps.map((step, idx) => {
            const activation = stepActivation(idx);
            const isLast = idx === steps.length - 1;
            return (
              <div key={step.num} className="flex items-start gap-3">
                <div className="relative w-9 shrink-0">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-full border bg-card font-data text-xs font-semibold"
                    style={{
                      borderColor:
                        activation > 0.5
                          ? "var(--foreground)"
                          : "var(--border)",
                    }}
                  >
                    {step.num}
                  </div>
                  {!isLast && (
                    <div className="absolute left-1/2 top-9 h-[calc(100%+0.9rem)] w-[2px] -translate-x-1/2 rounded-full bg-border" />
                  )}
                </div>

                <article className="flex-1 rounded-xl border bg-card/96 p-4">
                  <div className="mb-2.5 flex items-center gap-2.5">
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${accents[idx]}1A` }}
                    >
                      <step.Icon className="h-3.5 w-3.5 text-foreground/80" />
                    </div>
                    <h3 className="text-base font-semibold leading-tight text-foreground">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
